import React from 'react';
import { 
    FaTimes, FaUser, FaMapMarkerAlt, FaCreditCard, FaBoxOpen, 
    FaEnvelope, FaPhone, FaRupeeSign, FaShippingFast
} from 'react-icons/fa';

interface ViewOrderDetailsProps {
    order: any;
    onClose: () => void;
}

const ViewOrderDetails: React.FC<ViewOrderDetailsProps> = ({ order, onClose }) => {
    if (!order) return null; // Defensive check: Agar order null hai to render mat karo

    // 🔥 SMART DATA EXTRACTION (Handles CRM, Website, and Dummy Data safely)
    const customerName = 
        order?.customer?.name || 
        order?.customerName || 
        (order?.user?.firstName ? `${order.user.firstName} ${order.user.lastName || ''}` : null) || 
        (order?.mainUser?.firstName ? `${order.mainUser.firstName} ${order.mainUser.lastName || ''}` : null) || 
        'Unknown Customer';

    const customerEmail = order?.customer?.email || order?.user?.email || order?.mainUser?.email || order?.email || 'N/A';
    const customerMobile = order?.customer?.mobile || order?.user?.mobile || order?.mainUser?.mobile || order?.mobile || 'N/A';

    const shippingAddress = 
        order?.shippingAddressSnapshot || 
        order?.deliveryAddress || 
        (order?.houseNumber ? `${order.houseNumber}, ${order.streetArea || ''}, ${order.city || ''}, ${order.state || ''} - ${order.pincode || ''}` : 'Address not available');

    // Handle both Array products (Website) and Flat products (CRM)
    const orderItems = order?.orderItems || order?.products || (order?.medicine ? [{
        productName: order.medicine,
        quantity: order.medicineQuantity || 1,
        price: order.medicinePrice || order.payableAmount,
        imageUrl: null
    }] : []);

    const totalAmount = order?.totalAmount || order?.payableAmount || 0;
    const paymentMode = order?.paymentMode || 'COD';
    const status = order?.status || order?.orderStatus || 'PENDING';

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-900 rounded-[2rem] w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl border-2 border-gray-100 dark:border-gray-800">
                
                {/* Header */}
                <div className="sticky top-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md px-8 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center z-10">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
                            <FaBoxOpen className="text-blue-600 dark:text-blue-400" /> Order Details
                        </h2>
                        <p className="text-xs text-gray-500 font-mono mt-1 font-bold">ID: {order.id || 'N/A'}</p>
                    </div>
                    <button onClick={onClose} className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 hover:text-red-500 transition-all">
                        <FaTimes size={24} />
                    </button>
                </div>

                <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        {/* Customer Info */}
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-[11px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                                    <FaUser /> Customer Profile
                                </h3>
                                <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-inner">
                                    <p className="text-lg font-black text-gray-900 dark:text-white">{customerName}</p>
                                    <div className="mt-3 space-y-1.5">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                            <FaEnvelope className="text-gray-400" /> {customerEmail}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                                            <FaPhone className="text-gray-400" /> {customerMobile}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Address */}
                            <div>
                                <h3 className="text-[11px] font-black text-red-500 uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                                    <FaMapMarkerAlt /> Delivery Location
                                </h3>
                                <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                                    {shippingAddress}
                                </div>
                            </div>
                        </div>

                        {/* Order Info */}
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-[11px] font-black text-emerald-600 uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                                    <FaCreditCard /> Order Summary
                                </h3>
                                <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-inner space-y-4">
                                    <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-700">
                                        <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Status</span>
                                        <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                            {status.replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Payment Mode</span>
                                        <span className="text-sm font-black text-gray-900 dark:text-white">{paymentMode}</span>
                                    </div>
                                    {order.waybill && (
                                         <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Tracking AWB</span>
                                            <span className="text-sm font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded">{order.waybill}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-end pt-4 border-t-2 border-dashed border-gray-200 dark:border-gray-700 mt-2">
                                        <span className="text-sm font-black text-gray-600 dark:text-gray-400">Total Amount</span>
                                        <span className="text-3xl font-black text-emerald-600 dark:text-emerald-400 flex items-center">
                                            <FaRupeeSign size={20} /> {totalAmount}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Order Items Table */}
                    <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-800">
                        <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-[0.3em] mb-5 flex items-center gap-2">
                            <FaBoxOpen /> Product Details
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                            {orderItems.length > 0 ? orderItems.map((item: any, idx: number) => (
                                <div key={idx} className="flex flex-col sm:flex-row items-center gap-4 bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-blue-300 transition-colors">
                                    {item.imageUrl ? (
                                        <img src={item.imageUrl} className="w-16 h-16 rounded-lg object-cover bg-gray-50 border border-gray-100" alt="product" />
                                    ) : (
                                        <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 flex justify-center items-center text-gray-400">
                                            <FaBoxOpen size={24} />
                                        </div>
                                    )}
                                    <div className="flex-1 text-center sm:text-left min-w-0">
                                        <p className="text-sm font-black text-gray-900 dark:text-white truncate">{item.productName || 'Unknown Product'}</p>
                                        <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-wider">
                                            Qty: {item.quantity || 1} <span className="mx-1">|</span> Rate: ₹{item.price || item.pricePerUnit || 0}
                                        </p>
                                    </div>
                                    <div className="text-lg font-black text-gray-900 dark:text-white pr-2">
                                        ₹{(item.quantity || 1) * (item.price || item.pricePerUnit || 0)}
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center p-6 text-gray-400 font-medium">No items found for this order.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                    <button 
                        onClick={onClose} 
                        className="px-8 py-2.5 bg-gray-900 dark:bg-gray-700 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black dark:hover:bg-gray-600 transition-all shadow-md active:scale-95"
                    >
                        Close Window
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewOrderDetails;